import { useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdaugaTargetareTinte } from "@/hooks/api/mutate/useAdaugaTargetareTinte";
import { useSincronizeazaTargetareActivitate } from "@/hooks/api/mutate/useSincronizeazaTargetareActivitate";
import { useGetTargetareActivitate } from "@/hooks/api/query/useGetTargetareActivitate";
import { useToast } from "@/hooks/use-toast";
import { formatNumar } from "@/utils/targetareFormatting";
import {
  definitieRaportActivitate,
  randuriActivitate,
} from "@/utils/targetareRaportActivitate";
import { celulaActivitate } from "../components/activitate/CelulaActivitate";
import { FisaFirmaDialog } from "../components/FisaFirmaDialog";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";

export default function ActivitateTargetare() {
  const { toast } = useToast();
  const activitate = useGetTargetareActivitate();
  const sincronizeaza = useSincronizeazaTargetareActivitate();
  const adauga = useAdaugaTargetareTinte();
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);

  const randuri = useMemo(
    () => randuriActivitate(activitate.data?.firme ?? []),
    [activitate.data]
  );

  const adaugaTinte = (cuis: string[]) =>
    adauga.mutate(
      {
        cui: cuis,
        sursa: "activitate",
        note: "utilizator activ pe nis2pass.ro",
      },
      {
        onSuccess: (r) =>
          toast({
            title: "Ținte actualizate",
            description: `${formatNumar(r.data.adaugate)} firme noi în evidență, ${formatNumar(r.data.existente)} erau deja.`,
          }),
        onError: (e) =>
          toast({
            title: "Adăugarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );

  const celula = useMemo(
    () =>
      celulaActivitate({
        onFisa: setCuiSelectat,
        onAdaugaTinta: (cui) => adaugaTinte([cui]),
        adaugaInCurs: adauga.isPending,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adauga.isPending]
  );

  const date = activitate.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">
            Activitatea din platforma NIS2PASS
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Toate sesiunile deschise de utilizatorii platformei, agregate pe
            firmă: utilizatori, sesiuni, rezultatul încadrării și nivelul de
            risc din analiză, față de estimarea din universul de targetare.
            Firmele care lipsesc din univers sunt adăugate automat cu originea
            „Platforma NIS2PASS”, iar contactele declarate pot fi preluate în
            evidența de contacte.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={sincronizeaza.isPending}
          onClick={() =>
            sincronizeaza.mutate(undefined, {
              onSuccess: (r) =>
                toast({
                  title: "Platformă sincronizată",
                  description: `${formatNumar(r.data.firme_adaugate ?? 0)} firme noi în univers; ${formatNumar(r.data.emailuri)} emailuri, ${formatNumar(r.data.telefoane)} telefoane, ${formatNumar(r.data.situri)} site-uri preluate.`,
                }),
              onError: (e) =>
                toast({
                  title: "Sincronizarea a eșuat",
                  description: e.message,
                  variant: "destructive",
                }),
            })
          }
          title="Adaugă firmele lipsă în univers și preia emailul utilizatorului și datele de contact declarate, cu sursa Platforma NIS2PASS"
        >
          {sincronizeaza.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Sincronizează firmele și contactele din platformă
        </Button>
      </div>

      {activitate.isLoading ? (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        date && (
          <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
            {[
              ["Utilizatori înregistrați", date.utilizatori],
              ["Utilizatori cu sesiuni", date.utilizatori_cu_sesiuni],
              ["Sesiuni", date.sesiuni],
              ["Firme evaluate", date.firme.length],
              ["Sesiuni fără firmă", date.sesiuni_fara_firma],
            ].map(([eticheta, valoare]) => (
              <Card key={String(eticheta)}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {eticheta}
                  </p>
                  <p className="text-2xl font-heading font-semibold tabular-nums">
                    {formatNumar(Number(valoare))}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {activitate.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Activitatea nu a putut fi încărcată: {activitate.error.message}
        </p>
      )}

      <RaportInteractiv
        definitie={definitieRaportActivitate}
        randuri={randuri}
        isLoading={activitate.isLoading}
        selectat={cuiSelectat}
        onSelect={(cui) => setCuiSelectat(cui)}
        celula={celula}
        actiuniSelectie={[
          {
            eticheta: "Adaugă selecția la ținte",
            onClick: (cuis) => adaugaTinte(cuis),
          },
        ]}
      />

      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
    </div>
  );
}
